import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Plus, Trash2, ChevronRight, Info, GripVertical, Pencil, Check, X, ChevronUp, ChevronDown, Archive, RotateCcw, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { orderedTokens, resolveToken, unattachedCatalogFields, CT, XT } from "./fieldConfig";
import { InfoPopover } from "../../pages/vendor/parametrages/shared";

const CUSTOM_TYPES = [
  ["text", "Texte"], ["textarea", "Zone de texte"], ["checkbox", "Case à cocher"],
  ["select", "Liste déroulante"], ["tags", "Libellés"], ["number", "Nombre"],
  ["slider", "Curseur"], ["url", "Lien / URL"], ["date", "Date"],
  ["images", "Images"], ["files", "Fichiers"],
];
const TYPE_LABEL = Object.fromEntries(CUSTOM_TYPES);
const genFieldId = () => `field_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;

export default function TypeFieldsConfig({ typeValue, typeLabel, categories = [], fields, fieldCatalog = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(false);
  const [editing, setEditing] = useState(null); // token en cours de renommage
  const [draft, setDraft] = useState("");
  const [dragTok, setDragTok] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null); // null | [{label,input,options,reason,_selected}]
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addBtnRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const openAddMenu = () => {
    const r = addBtnRef.current?.getBoundingClientRect();
    if (r) {
      const width = 256;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setMenuPos({ left, top: r.bottom + 4, width });
    }
    setShowAddMenu(true);
  };

  const cfg = fields || { catalog: {}, custom: [], order: [] };
  const catalogCfg = cfg.catalog || {};
  const custom = cfg.custom || [];
  const archived = custom.filter((c) => c.archived);

  // Phase 1 — champs ACTIFS d'abord, puis inactifs ; ordre personnalisé conservé dans chaque groupe.
  const rawTokens = orderedTokens(cfg, fieldCatalog);
  const resolved = rawTokens.map((t) => ({ t, d: resolveToken(t, cfg, fieldCatalog) })).filter((x) => x.d);
  const tokens = [...resolved.filter((x) => x.d.enabled), ...resolved.filter((x) => !x.d.enabled)].map((x) => x.t);
  const rows = tokens.map((t) => resolveToken(t, cfg, fieldCatalog)).filter(Boolean);
  const activeCount = rows.filter((r) => r.enabled).length;

  const emit = (patch) => onChange({ catalog: catalogCfg, custom, order: tokens, ...patch });

  const setOrder = (newTokens) => emit({ order: newTokens });
  const patchCatalog = (key, patch) => emit({ catalog: { ...catalogCfg, [key]: { ...(catalogCfg[key] || {}), ...patch } } });
  const patchCustom = (id, patch) => emit({ custom: custom.map((c) => (c.id === id ? { ...c, ...patch } : c)) });

  const addCustom = () => {
    const id = genFieldId();
    emit({
      custom: [...custom, { id, label: "Nouveau champ", type: "text", enabled: true, archived: false, options: [] }],
      order: [...tokens, XT(id)],
    });
    setShowAddMenu(false);
    toast.success("Champ ajouté");
  };

  // Attacher un champ du catalogue (depuis la liste) — Phase 5
  const attachCatalogField = (key) => {
    emit({ catalog: { ...catalogCfg, [key]: { enabled: true } }, order: [...tokens, CT(key)] });
    setShowAddMenu(false);
    toast.success("Champ ajouté");
  };

  // ── Suggestion de champs par le Copilote (déclenchée au clic uniquement) ──
  const existingLabels = () => {
    const cat = Object.entries(catalogCfg);
    const catLabels = fieldCatalog.map((f) => (catalogCfg[f.key]?.label || f.label));
    const customLabels = custom.filter((c) => !c.archived).map((c) => c.label);
    return [...catLabels, ...customLabels];
  };
  const suggestFields = async () => {
    setAiLoading(true);
    setAiSuggestions(null);
    try {
      const { data } = await api.post("/vendor/copilot/suggest-fields", {
        type_label: typeLabel || typeValue || "bien",
        categories: categories,
        existing_labels: existingLabels(),
      });
      const sugg = (data.suggestions || []).map((s) => ({ ...s, _selected: true }));
      if (!sugg.length) { toast.error("Aucune suggestion"); return; }
      setAiSuggestions(sugg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Le Copilote n'a pas pu répondre");
    } finally {
      setAiLoading(false);
    }
  };
  const addSelectedSuggestions = () => {
    const chosen = (aiSuggestions || []).filter((s) => s._selected);
    if (!chosen.length) { toast.error("Sélectionnez au moins un champ"); return; }
    const existing = existingLabels().map((l) => l.toLowerCase());
    const toAdd = [];
    const newTokens = [...tokens];
    for (const s of chosen) {
      if (existing.includes(s.label.toLowerCase())) continue; // éviter les doublons
      const id = genFieldId();
      toAdd.push({ id, label: s.label, type: s.input || "text", enabled: true, archived: false, options: s.options || [] });
      newTokens.push(XT(id));
      existing.push(s.label.toLowerCase());
    }
    if (!toAdd.length) { toast.error("Ces champs existent déjà"); setAiSuggestions(null); return; }
    emit({ custom: [...custom, ...toAdd], order: newTokens });
    setAiSuggestions(null);
    toast.success(`${toAdd.length} champ(s) ajouté(s)`);
  };

  const archiveCustom = (c) => {
    if (!window.confirm(`Archiver le champ « ${c.label} » ? Il sera masqué des formulaires, mais les valeurs déjà saisies sur les biens sont conservées. Vous pourrez le restaurer à tout moment.`)) return;
    emit({ custom: custom.map((x) => (x.id === c.id ? { ...x, archived: true, enabled: false } : x)), order: tokens.filter((t) => t !== XT(c.id)) });
    toast.success("Champ archivé (données conservées)");
  };
  const restoreCustom = (c) => {
    emit({ custom: custom.map((x) => (x.id === c.id ? { ...x, archived: false, enabled: true } : x)), order: [...tokens, XT(c.id)] });
    toast.success("Champ restauré");
  };
  const deleteCustom = (c) => {
    const txt = window.prompt(`SUPPRESSION DÉFINITIVE du champ « ${c.label} ».\nLes valeurs déjà saisies pour ce champ sur vos biens seront définitivement perdues.\nPour confirmer, tapez SUPPRIMER :`);
    if (txt !== "SUPPRIMER") return;
    emit({ custom: custom.filter((x) => x.id !== c.id), order: tokens.filter((t) => t !== XT(c.id)) });
    toast.success("Champ supprimé");
  };

  // Renommage inline (stylo → coche / échap)
  const startRename = (row) => { setEditing(row.token); setDraft(row.label); };
  const cancelRename = () => { setEditing(null); setDraft(""); };
  const saveRename = (row) => {
    const label = draft.trim();
    if (!label) { toast.error("Le libellé ne peut pas être vide"); return; }
    if (row.kind === "catalog") patchCatalog(row.key, { label });
    else patchCustom(row.id, { label });
    setEditing(null); setDraft("");
    toast.success("Libellé mis à jour");
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= tokens.length) return;
    const next = [...tokens];
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  };
  const onDrop = (targetTok) => {
    if (!dragTok || dragTok === targetTok) return;
    const next = tokens.filter((t) => t !== dragTok);
    const ti = next.indexOf(targetTok);
    next.splice(ti, 0, dragTok);
    setOrder(next);
    setDragTok(null);
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white" data-testid={`fleet-fields-config-${typeValue}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
        <span className="text-sm font-semibold text-slate-800">Propriétés</span>
        <span className="relative inline-flex" onClick={(e) => { e.stopPropagation(); setInfo(true); }} role="button" tabIndex={0} aria-label="Aide propriétés">
          <Info className="h-3.5 w-3.5 text-slate-400 hover:text-violet-600" />
        </span>
        <span className="ml-auto text-[11px] text-slate-400">{activeCount} actif(s)</span>
      </button>
      <InfoPopover open={info} onClose={() => setInfo(false)} title="Propriétés"
        points={[
          "Les propriétés décrivent les informations saisies pour chaque bien de ce type (Nom interne, Marque, Modèle, Année, Couleur…).",
          "Activez ou masquez une propriété, renommez-la (stylo) et réordonnez-la par glisser-déposer.",
          "Ajoutez vos propres propriétés avec « Ajouter un champ », ou laissez le Copilote vous en suggérer.",
        ]} />

      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          {rows.map((row, idx) => {
            const isEditing = editing === row.token;
            const isCustom = row.kind === "custom";
            const c = isCustom ? custom.find((x) => x.id === row.id) : null;
            const testKey = isCustom ? row.id : `${typeValue}-${row.key}`;
            return (
              <div
                key={row.token}
                draggable={!isEditing}
                onDragStart={() => setDragTok(row.token)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(row.token)}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${isCustom ? "border-violet-200 bg-violet-50/30" : row.enabled ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/60"}`}
                data-testid={isCustom ? `fleet-custom-field-${row.id}` : `fleet-field-${typeValue}-${row.key}`}
              >
                {/* Poignée + up/down (mobile) */}
                <div className="flex flex-col items-center shrink-0">
                  <GripVertical className="h-3.5 w-3.5 text-slate-300 cursor-grab" data-testid={`fleet-field-drag-${testKey}`} />
                </div>
                <div className="flex flex-col shrink-0 -my-1">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="h-3.5 text-slate-300 hover:text-violet-600 disabled:opacity-30" data-testid={`fleet-field-up-${testKey}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="h-3.5 text-slate-300 hover:text-violet-600 disabled:opacity-30" data-testid={`fleet-field-down-${testKey}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                </div>

                {/* Toggle actif/inactif */}
                <button type="button" onClick={() => (isCustom ? patchCustom(row.id, { enabled: !row.enabled }) : patchCatalog(row.key, { enabled: !row.enabled }))}
                  className={`h-5 w-9 rounded-full transition-colors shrink-0 ${row.enabled ? "bg-violet-600" : "bg-slate-300"}`}
                  data-testid={isCustom ? `fleet-custom-toggle-${row.id}` : `fleet-field-toggle-${typeValue}-${row.key}`} aria-label="Activer/désactiver">
                  <span className={`block h-4 w-4 bg-white rounded-full transition-transform mt-0.5 ${row.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>

                {/* Libellé : lecture + stylo, ou édition inline */}
                {isEditing ? (
                  <div className="flex-1 min-w-0 flex items-center gap-1">
                    <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(row); if (e.key === "Escape") cancelRename(); }}
                      className="flex-1 min-w-0 h-8 px-2 rounded-md border border-violet-300 bg-white text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      data-testid={isCustom ? `fleet-custom-label-${row.id}` : `fleet-field-label-${typeValue}-${row.key}`} />
                    <button type="button" onClick={() => saveRename(row)} className="h-7 w-7 rounded-md bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 shrink-0" data-testid={`fleet-field-rename-save-${testKey}`}><Check className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={cancelRename} className="h-7 w-7 rounded-md border border-slate-200 text-slate-400 flex items-center justify-center hover:bg-slate-50 shrink-0" data-testid={`fleet-field-rename-cancel-${testKey}`}><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                    <span className={`text-xs truncate ${row.enabled ? "text-slate-700" : "text-slate-400"}`} data-testid={`fleet-field-name-${testKey}`}>{row.label}</span>
                    <button type="button" onClick={() => startRename(row)} className="h-6 w-6 rounded-md text-slate-300 hover:text-violet-600 hover:bg-slate-50 flex items-center justify-center shrink-0" data-testid={`fleet-field-rename-${testKey}`} aria-label="Renommer"><Pencil className="h-3 w-3" /></button>
                  </div>
                )}

                {/* Paramètres champ perso : type, options, bornes slider */}
                {isCustom && !isEditing && (
                  <>
                    <select value={c.type} onChange={(e) => patchCustom(row.id, { type: e.target.value })}
                      className="h-8 px-1.5 rounded-md border border-slate-200 bg-white text-[11px] shrink-0" data-testid={`fleet-custom-type-${row.id}`}>
                      {CUSTOM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    {(c.type === "select" || c.type === "tags") && (
                      <input value={(c.options || []).join(", ")} onChange={(e) => patchCustom(row.id, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                        className="h-8 px-2 rounded-md border border-slate-200 bg-white text-[11px] w-28" placeholder="option1, option2" data-testid={`fleet-custom-options-${row.id}`} />
                    )}
                    {c.type === "slider" && (
                      <div className="flex items-center gap-1 shrink-0">
                        <input type="number" value={c.min ?? 0} onChange={(e) => patchCustom(row.id, { min: Number(e.target.value) })} className="h-8 w-12 px-1 rounded-md border border-slate-200 text-[11px]" placeholder="min" data-testid={`fleet-custom-min-${row.id}`} />
                        <input type="number" value={c.max ?? 100} onChange={(e) => patchCustom(row.id, { max: Number(e.target.value) })} className="h-8 w-12 px-1 rounded-md border border-slate-200 text-[11px]" placeholder="max" data-testid={`fleet-custom-max-${row.id}`} />
                        <input type="number" value={c.step ?? 1} onChange={(e) => patchCustom(row.id, { step: Number(e.target.value) || 1 })} className="h-8 w-12 px-1 rounded-md border border-slate-200 text-[11px]" placeholder="pas" data-testid={`fleet-custom-step-${row.id}`} />
                      </div>
                    )}
                    <button type="button" onClick={() => archiveCustom(c)} className="h-8 w-8 rounded-md text-slate-400 hover:bg-amber-50 hover:text-amber-600 flex items-center justify-center shrink-0" data-testid={`fleet-custom-archive-${row.id}`} aria-label="Archiver"><Archive className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => deleteCustom(c)} className="h-8 w-8 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center shrink-0" data-testid={`fleet-custom-remove-${row.id}`} aria-label="Supprimer"><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
                {!isCustom && !isEditing && (
                  <span className="text-[10px] text-slate-300 uppercase tracking-wider shrink-0 w-12 text-right">{row.input}</span>
                )}
              </div>
            );
          })}

          <div className="mt-1 flex flex-wrap items-center gap-2">
            <div className="relative">
              <button ref={addBtnRef} type="button" onClick={() => (showAddMenu ? setShowAddMenu(false) : openAddMenu())}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50" data-testid={`fleet-add-field-${typeValue}`}>
                <Plus className="h-3 w-3" /> Ajouter un champ
              </button>
              {showAddMenu && menuPos && createPortal(
                <>
                  <div className="fixed inset-0 z-[80]" onClick={() => setShowAddMenu(false)} />
                  <div style={{ position: "fixed", left: menuPos.left, top: menuPos.top, width: menuPos.width }}
                    className="z-[81] max-h-72 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl p-1.5" data-testid={`fleet-add-field-menu-${typeValue}`}>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">Depuis le catalogue</div>
                    {unattachedCatalogFields(cfg, fieldCatalog).length === 0 ? (
                      <div className="px-2.5 py-1.5 text-[11px] text-slate-400">Tous les champs du catalogue sont déjà ajoutés.</div>
                    ) : (
                      unattachedCatalogFields(cfg, fieldCatalog).map((f) => (
                        <button key={f.key} type="button" onClick={() => attachCatalogField(f.key)}
                          className="w-full text-left px-2.5 py-1.5 rounded-lg text-xs text-slate-700 hover:bg-violet-50 hover:text-violet-700" data-testid={`fleet-attach-${typeValue}-${f.key}`}>
                          {f.label}
                        </button>
                      ))
                    )}
                    <div className="border-t border-slate-100 my-1" />
                    <button type="button" onClick={addCustom}
                      className="w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-semibold text-violet-700 hover:bg-violet-50" data-testid={`fleet-add-custom-field-${typeValue}`}>
                      + Champ personnalisé
                    </button>
                  </div>
                </>,
                document.body
              )}
            </div>
            <button type="button" onClick={suggestFields} disabled={aiLoading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-violet-200 bg-violet-50/60 text-violet-700 text-xs font-semibold hover:bg-violet-100 disabled:opacity-60" data-testid={`fleet-suggest-fields-${typeValue}`}>
              {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Suggérer des champs avec le Copilote
            </button>
          </div>

          {aiSuggestions && (
            <div className="mt-2 rounded-xl border border-violet-200 bg-violet-50/40 p-3" data-testid={`fleet-ai-suggestions-${typeValue}`}>
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                <span className="text-xs font-bold text-slate-800">Suggestions du Copilote pour « {typeLabel || typeValue} »</span>
                <button type="button" onClick={() => setAiSuggestions((prev) => { const allOn = prev.every((x) => x._selected); return prev.map((x) => ({ ...x, _selected: !allOn })); })}
                  className="ml-auto text-[11px] font-semibold text-violet-700 hover:text-violet-900" data-testid={`fleet-ai-toggle-all-${typeValue}`}>
                  {aiSuggestions.every((x) => x._selected) ? "Tout désactiver" : "Tout activer"}
                </button>
                <button type="button" onClick={() => setAiSuggestions(null)} className="h-6 w-6 rounded-md text-slate-400 hover:bg-white flex items-center justify-center" data-testid={`fleet-ai-close-${typeValue}`}><X className="h-3.5 w-3.5" /></button>
              </div>
              <div className="space-y-1.5">
                {aiSuggestions.map((s, i) => (
                  <label key={i} className="flex items-start gap-2 rounded-lg bg-white border border-slate-100 px-2.5 py-2 cursor-pointer" data-testid={`fleet-ai-suggestion-${typeValue}-${i}`}>
                    <input type="checkbox" checked={s._selected} onChange={(e) => setAiSuggestions((prev) => prev.map((x, j) => j === i ? { ...x, _selected: e.target.checked } : x))}
                      className="mt-0.5 h-4 w-4 accent-violet-600 shrink-0" data-testid={`fleet-ai-suggestion-check-${typeValue}-${i}`} />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-800">{s.label} <span className="text-[10px] font-normal text-slate-400">· {TYPE_LABEL[s.input] || s.input}{s.options?.length ? ` (${s.options.join(", ")})` : ""}</span></div>
                      {s.reason && <div className="text-[11px] text-slate-500 leading-snug">{s.reason}</div>}
                    </div>
                  </label>
                ))}
              </div>
              <button type="button" onClick={addSelectedSuggestions}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700" data-testid={`fleet-ai-add-selected-${typeValue}`}>
                <Plus className="h-3 w-3" /> Ajouter la sélection
              </button>
            </div>
          )}

          {archived.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Champs archivés ({archived.length})</div>
              {archived.map((c) => (
                <div key={c.id} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-1.5" data-testid={`fleet-archived-field-${c.id}`}>
                  <span className="flex-1 min-w-0 text-xs text-slate-400 truncate">{c.label} <span className="text-[10px]">· {TYPE_LABEL[c.type] || c.type}</span></span>
                  <button type="button" onClick={() => restoreCustom(c)} className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 text-[11px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-600" data-testid={`fleet-restore-field-${c.id}`}><RotateCcw className="h-3 w-3" /> Restaurer</button>
                  <button type="button" onClick={() => deleteCustom(c)} className="h-7 w-7 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center" data-testid={`fleet-archived-remove-${c.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
